import { useState, useRef } from "react";
import * as XLSX from 'xlsx';

export function CustomTooltip({ active, payload, label, prefix = '' }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white border border-slate-100 shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-600 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">
          {p.name}:{' '}
          {prefix}
          {typeof p.value === 'number'
            ? p.value.toLocaleString('en-US', {
                minimumFractionDigits:
                  p.name?.toLowerCase().includes('revenue') || p.name?.toLowerCase().includes('amount') ? 2 : 0,
              })
            : p.value}
        </p>
      ))}
    </div>
  );
}

const STATUS_COLORS = {
  Completed: '#10b981',
  Pending: '#f59e0b',
  Cancelled: '#ef4444',
  Processing: '#3b82f6',
  Draft: '#8b5cf6',
  Shipped: '#06b6d4',
  'Not Shipped': '#f97316',
  'Partially Shipped': '#a78bfa',
  Paid: '#10b981',
  Unpaid: '#ef4444',
  'Partially Paid': '#f59e0b',
  Refunded: '#6b7280',
  Fulfilled: '#10b981',
  'Partially Fulfilled': '#f59e0b',
  Unfulfilled: '#ef4444',
};

export function KpiRow({ query }) {
  if (query.error) return <ApiError error={query.error} label="KPIs" />;
  const d = query.data;
  const tiles = [
    { label: "Total Revenue", value: d ? formatCurrency(d.totalRevenue) : "—", accent: "from-indigo-500 to-violet-600", icon: "$" },
    { label: "Total Orders", value: d ? formatNumber(d.totalOrders) : "—", accent: "from-emerald-500 to-teal-600", icon: "🛒" },
    { label: "Avg Order Value", value: d ? formatCurrency(d.avgOrderValue) : "—", accent: "from-amber-500 to-orange-600", icon: "≈" },
    { label: "Total Units Sold", value: d ? formatNumber(d.totalUnitsSold || 0) : "—", accent: "from-rose-500 to-pink-600", icon: "📦" },
  ];
  return (
    <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {tiles.map((t) => <KpiTile key={t.label} loading={query.loading} {...t} />)}
    </section>
  );
}


export function getColor(key) {
  return (
    STATUS_COLORS[key] ??
    `hsl(${Math.abs(
      (key || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
    ) % 360}, 65%, 55%)`
  );
}

export function formatCurrencyBkp(value) {
  if (value === null || value === undefined) return '$0.00';

  const absValue = Math.abs(value);

  let formattedNumber = value;
  let suffix = '';

  if (absValue >= 1_000_000_000) {
    formattedNumber = value / 1_000_000_000;
    suffix = 'B';
  } else if (absValue >= 1_000_000) {
    formattedNumber = value / 1_000_000;
    suffix = 'M';
  } else if (absValue >= 2_000) {
    formattedNumber = value / 1_000;
    suffix = 'k';
  }

  const numberString =
    suffix
      ? formattedNumber.toFixed(1).replace(/\.0$/, '') + suffix
      : new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);

  return `$${numberString}`;
}


export const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

/*
export function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value ?? 0);
}
*/

export const formatNumber = (n) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(Number(n)) || 0);
  
export function formatNumberBkp(value) {
  return new Intl.NumberFormat('en-US').format(value ?? 0);
}

export function formatCompactNumber(value) {
  if (value === null || value === undefined || isNaN(value)) return '0';

  const abs = Math.abs(value);

  if (abs >= 1e9) return (value / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6) return (value / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return (value / 1e3).toFixed(1) + 'k';

  return value.toLocaleString();
}

export const getYearColor = (index) => {
  const colors = ['#10b981', '#f163e5', '#f59e0b'];
  return colors[index];
};


/** Helper: Format JS Date to YYYY-MM-DD (Fix: Use Local Time, not UTC) */
export const formatToInputDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** Helper: Format Date for display (Fix: Parse YYYY-MM-DD as Local Time) */
export const formatDisplayDate = (date) => {
  if (!date) return "";
  let d;
  if (typeof date === "string" && date.includes("-")) {
    const [year, month, day] = date.split("-").map(Number);
    d = new Date(year, month - 1, day); // Forces local time parsing
  } else {
    d = new Date(date);
  }
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Helper: Get preset date ranges (Fix: Reset hours to prevent drift) */
export const getPresetRange = (preset) => {
  const today = new Date();
  const start = new Date();
  start.setHours(0, 0, 0, 0); // Normalize to start of day

  switch (preset) {
    case "MTD":
      start.setDate(1);
      break;
    case "QTD":
      const quarter = Math.floor(today.getMonth() / 3);
      start.setMonth(quarter * 3, 1);
      break;
    case "YTD":
      start.setMonth(0, 1);
      break;
    case "12M":
      start.setFullYear(today.getFullYear() - 1);
      break;
    default:
      return { start: null, end: null };
  }

  return { start, end: today };
};

/** Compute default YTD range */
export const getDefaultYTD = () => {
  const range = getPresetRange("YTD");
  return {
    from: formatToInputDate(range.start),
    to: formatToInputDate(range.end),
  };
};

export const DEFAULT_FILTERS = {
  ...getDefaultYTD(),
  family: "All Families",
  customer: "All Customers",
  owner: "All Owners",
  country: "All",
  region: "All Regions",
  channel: "All",
};

export const GOV_TAB = {
  "gov:customers": "customers",
  "gov:products": "products",
  "gov:categories": "categories",
  "gov:formats": "formats",
};

// ✅ Chart color palette
export const CHART_COLORS = Array.from({ length: 1750 }, (_, i) => {
  const hue = (i * 137.508) % 360;
  const saturation = 65 + (i % 3) * 10;
  const lightness = 45 + (i % 4) * 8;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
});

/**
 * Optimized Donut Chart
 */
export function DonutChartWithTooltip({ data, centerLabel, centerValue }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);

  const FALLBACK_PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const size = 220;
  const outerRadius = 95;
  const innerRadius = 60;
  const cx = size / 2;
  const cy = size / 2;

  let cumulativeAngle = -90;

  const slices = data.map((item, index) => {
    // ... your existing slice calculation code (unchanged) ...
    const rawAngle = (item.value / total) * 360;
    const angle = rawAngle >= 360 ? 359.99 : rawAngle;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + outerRadius * Math.cos(startRad);
    const y1 = cy + outerRadius * Math.sin(startRad);
    const x2 = cx + outerRadius * Math.cos(endRad);
    const y2 = cy + outerRadius * Math.sin(endRad);
    const x3 = cx + innerRadius * Math.cos(endRad);
    const y3 = cy + innerRadius * Math.sin(endRad);
    const x4 = cx + innerRadius * Math.cos(startRad);
    const y4 = cy + innerRadius * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;
    const path = `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;

    const midAngle = (startAngle + endAngle) / 2;
    const midRad = (midAngle * Math.PI) / 180;
    const labelX = cx + ((outerRadius + innerRadius) / 2) * Math.cos(midRad);
    const labelY = cy + ((outerRadius + innerRadius) / 2) * Math.sin(midRad);

    cumulativeAngle = endAngle;

    return {
      ...item,
      path,
      percentage: (item.value / total) * 100,
      labelX,
      labelY,
      index,
      color: item.color || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
    };
  });

  // ✅ NEW: Smart tooltip positioning
  const getTooltipStyle = () => {
    const OFFSET = 15;
    const PADDING = 10; // safe distance from screen edges
    const TOOLTIP_WIDTH = 220;   // matches minWidth + a bit
    const TOOLTIP_HEIGHT = 140;  // approximate rendered height

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = mousePos.x + OFFSET;
    let top = mousePos.y + OFFSET;

    // If tooltip overflows right edge → flip to left of cursor
    if (left + TOOLTIP_WIDTH + PADDING > viewportWidth) {
      left = mousePos.x - TOOLTIP_WIDTH - OFFSET;
    }

    // If tooltip overflows bottom edge → flip above cursor
    if (top + TOOLTIP_HEIGHT + PADDING > viewportHeight) {
      top = mousePos.y - TOOLTIP_HEIGHT - OFFSET;
    }

    // Final safety clamp — never let it go off screen
    left = Math.max(PADDING, Math.min(left, viewportWidth - TOOLTIP_WIDTH - PADDING));
    top = Math.max(PADDING, Math.min(top, viewportHeight - TOOLTIP_HEIGHT - PADDING));

    return {
      left: `${left}px`,
      top: `${top}px`,
      minWidth: '200px',
      maxWidth: `${Math.min(TOOLTIP_WIDTH, viewportWidth - PADDING * 2)}px`,
    };
  };

  return (
    <div className="relative flex flex-col items-center w-full">
      <div className="relative">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          onMouseLeave={() => setHoveredIndex(null)}
          className="drop-shadow-sm"
        >
          {slices.map((slice) => {
            const strokeWidth = data.length > 30 ? "0.2" : data.length > 10 ? "0.8" : "2";
            return (
              <g key={slice.index}>
                <path
                  d={slice.path}
                  fill={slice.color}
                  stroke="white"
                  strokeWidth={strokeWidth}
                  className="cursor-pointer transition-all duration-300"
                  style={{
                    opacity: hoveredIndex === null || hoveredIndex === slice.index ? 1 : 0.4,
                    transform: hoveredIndex === slice.index ? 'scale(1.03)' : 'scale(1)',
                    transformOrigin: 'center',
                  }}
                  onMouseEnter={(e) => {
                    setHoveredIndex(slice.index);
                    setMousePos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                  // ✅ NEW: Touch support for mobile
                  onTouchStart={(e) => {
                    const touch = e.touches[0];
                    setHoveredIndex(slice.index);
                    setMousePos({ x: touch.clientX, y: touch.clientY });
                  }}
                  onTouchMove={(e) => {
                    const touch = e.touches[0];
                    setMousePos({ x: touch.clientX, y: touch.clientY });
                  }}
                  onTouchEnd={() => setHoveredIndex(null)}
                />
                {slice.percentage > 5 && (
                  <text
                    x={slice.labelX}
                    y={slice.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="800"
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {slice.percentage.toFixed(1)}%
                  </text>
                )}
              </g>
            );
          })}
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize="10" fill="#94a3b8" fontWeight="700" className="uppercase tracking-widest">{centerLabel}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="16" fill="#1e293b" fontWeight="800">{centerValue}</text>
        </svg>
      </div>

      {hoveredIndex !== null && (
        <div
          ref={tooltipRef}
          className="fixed z-[9999] pointer-events-none bg-white/95 backdrop-blur shadow-2xl rounded-xl px-4 py-3 border border-slate-200 ring-1 ring-black/5"
          style={getTooltipStyle()}
        >
          <div className="flex items-center gap-2 mb-2 border-b border-slate-100 pb-2">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: slices[hoveredIndex].color }} />
            <div className="text-xs font-black text-slate-600 uppercase tracking-tight truncate">
              {slices[hoveredIndex].label}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center gap-3">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Revenue</span>
              <span className="text-sm font-bold text-slate-900 truncate">
                {formatCurrency( slices[hoveredIndex].value || slices[hoveredIndex].revenue)}
              </span>
            </div>
            {/* ✅ NEW: Units Sold */}
            {slices[hoveredIndex].unitsSold !== undefined && (
              <div className="flex justify-between items-center gap-3">
                <span className="text-[10px] text-slate-500 font-bold uppercase">Units</span>
                <span className="text-sm font-bold text-slate-900 truncate">
                  {formatNumber(Math.round(slices[hoveredIndex].unitsSold))}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pt-1 mt-1 border-t border-slate-50 gap-3">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Share</span>
              <span className="text-sm font-black text-indigo-600">
                {slices[hoveredIndex].percentage.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DonutChartWithTooltipbkp({ data, centerLabel, centerValue }) {

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const FALLBACK_PALETTE = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const size = 220;
  const outerRadius = 95;
  const innerRadius = 60;
  const cx = size / 2;
  const cy = size / 2;

  let cumulativeAngle = -90;

  const slices = data.map((item, index) => {
    const rawAngle = (item.value / total) * 360;
    const angle = rawAngle >= 360 ? 359.99 : rawAngle;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = cx + outerRadius * Math.cos(startRad);
    const y1 = cy + outerRadius * Math.sin(startRad);
    const x2 = cx + outerRadius * Math.cos(endRad);
    const y2 = cy + outerRadius * Math.sin(endRad);
    const x3 = cx + innerRadius * Math.cos(endRad);
    const y3 = cy + innerRadius * Math.sin(endRad);
    const x4 = cx + innerRadius * Math.cos(startRad);
    const y4 = cy + innerRadius * Math.sin(startRad);

    const largeArc = angle > 180 ? 1 : 0;
    const path = `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;

    const midAngle = (startAngle + endAngle) / 2;
    const midRad = (midAngle * Math.PI) / 180;
    const labelX = cx + ((outerRadius + innerRadius) / 2) * Math.cos(midRad);
    const labelY = cy + ((outerRadius + innerRadius) / 2) * Math.sin(midRad);

    cumulativeAngle = endAngle;

    return { 
      ...item, 
      path, 
      percentage: (item.value / total) * 100, 
      labelX, 
      labelY, 
      index,
      color: item.color || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]
    };
  });

  return (
    <>
    <div className="relative flex flex-col items-center w-full">
      <div className="relative">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          onMouseLeave={() => setHoveredIndex(null)}
          className="drop-shadow-sm"
        >
          {slices.map((slice) => {
            const strokeWidth = data.length > 30 ? "0.2" : data.length > 10 ? "0.8" : "2";
            return (
              <g key={slice.index}>
                <path
                  d={slice.path}
                  fill={slice.color}
                  stroke="white"
                  strokeWidth={strokeWidth}
                  className="cursor-pointer transition-all duration-300"
                  style={{
                    opacity: hoveredIndex === null || hoveredIndex === slice.index ? 1 : 0.4,
                    transform: hoveredIndex === slice.index ? 'scale(1.03)' : 'scale(1)',
                    transformOrigin: 'center',
                  }}
                  onMouseEnter={(e) => {
                    setHoveredIndex(slice.index);
                    setMousePos({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                />
                {/* Only show percentage on large enough slices */}
                {slice.percentage > 5 && (
                  <text
                    x={slice.labelX}
                    y={slice.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="800"
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                  >
                    {slice.percentage.toFixed(1)}%
                  </text>
                )}                
              </g>
            );
          })}
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize="10" fill="#94a3b8" fontWeight="700" className="uppercase tracking-widest">{centerLabel}</text>
          <text x={cx} y={cy + 12} textAnchor="middle" fontSize="16" fill="#1e293b" fontWeight="800">{centerValue}</text>
        </svg>
      </div>

      {hoveredIndex !== null && (
        <div
          className="fixed z-[9999] pointer-events-none bg-white/95 backdrop-blur shadow-2xl rounded-xl px-4 py-3 border border-slate-200 ring-1 ring-black/5"
          style={{ left: mousePos.x + 15, top: mousePos.y + 15, minWidth: '200px' }}
        >
          <div className="flex items-center gap-2 mb-2 border-b border-slate-100 pb-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: slices[hoveredIndex].color }} />
            <div className="text-xs font-black text-slate-600 uppercase tracking-tight truncate">{slices[hoveredIndex].label}</div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Revenue</span>
              <span className="text-sm font-bold text-slate-900">{formatCurrency(slices[hoveredIndex].revenue)}</span>
            </div>
            <div className="flex justify-between items-center pt-1 mt-1 border-t border-slate-50">
              <span className="text-[10px] text-slate-500 font-bold uppercase">Share</span>
              <span className="text-sm font-black text-indigo-600">{slices[hoveredIndex].percentage.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export function SectionSkeleton({ withPie }) {
  return (
    <>
    <div className={`grid gap-6 ${withPie ? "lg:grid-cols-[280px_1fr]" : ""}`}>
      {withPie && <div className="h-48 w-48 mx-auto rounded-full bg-slate-100 animate-pulse" />}
      <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-10 w-full bg-slate-50 animate-pulse rounded" />)}
      </div>
    </div>
    </>
  );
}

export function EmptyState({ label }) {
  return <div className="py-12 text-center text-slate-400 text-sm italic">{label}</div>;
}

export function ApiError({ error, label }) {
  return (
    <div className="p-4 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
      <strong>Error loading {label}</strong>
    </div>
  );
}

export function DataTable({ columns, rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-100">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-500">
          <tr>
            {columns.map(c => <th key={c.key} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-slate-50/50">
              {columns.map(c => <td key={c.key} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function KpiTile({ label, value, delta, accent, icon, loading }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${accent} opacity-10`} />
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${accent} text-white shadow-sm`}>
          <span className="text-sm font-bold">{icon}</span>
        </div>
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900">{loading ? <Skeleton w="w-28" /> : value}</div>
      {delta && <div className="mt-1 text-xs font-medium text-slate-500">{delta}</div>}
    </div>
  );
}

export function SectionCard({ number, title, subtitle, onExport, rightSlot, children, exportExcel }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-start justify-between">
        <div className="flex gap-3">
          <span className="bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded h-fit">{number}</span>
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {rightSlot}
          {exportExcel && <button onClick={onExport} className="text-xs border px-3 py-1.5 rounded-md hover:bg-slate-50">Export</button>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function ShareBar({ pct, color }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-1.5 w-12 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-medium w-8">{Number(pct).toFixed(1)}%</span>
    </div>
  );
}

// ✅ Family share bar used in drill-down modal
export function FamilyShareBar({ pct, color }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-2 w-16 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(pct, 100)}%`, background: color }}
        />
      </div>
      <span className="text-xs font-bold text-slate-700 w-9 text-right">{pct}%</span>
    </div>
  );
}

export function ConfidenceBar({ pct }) {
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-1.5 w-12 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium w-8">{pct}%</span>
    </div>
  );
}

export function RankBadge({ n }) {
  const colors = n === 1 ? "bg-amber-100 text-amber-700" : n === 2 ? "bg-slate-100 text-slate-600" : "bg-transparent text-slate-400";
  return <span className={`w-6 h-6 inline-flex items-center justify-center rounded text-xs font-bold ${colors}`}>{n}</span>;
}

// ✅ Status badge for order status
export function StatusBadge({ status }) {
  const map = {
    Invoiced: "bg-blue-50 text-blue-700",
    "In Progress": "bg-amber-50 text-amber-700",
    Cancelled: "bg-rose-50 text-rose-700",
    Completed: "bg-emerald-50 text-emerald-700",
  };
  const cls = map[status] || "bg-slate-50 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>
      {status || "—"}
    </span>
  );
}

// ✅ Payment badge for payment status
export function PaymentBadge({ status }) {
  const map = {
    Paid: "bg-emerald-50 text-emerald-700",
    Unpaid: "bg-rose-50 text-rose-700",
    Partial: "bg-amber-50 text-amber-700",
  };
  const cls = map[status] || "bg-slate-50 text-slate-600";
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>
      {status || "—"}
    </span>
  );
}

export function TrendCell({ trend, pct }) {
  const isUp = trend === "up";
  return (
    <span className={`text-xs font-bold ${isUp ? "text-emerald-600" : "text-rose-600"}`}>
      {isUp ? "↑" : "↓"} {Math.abs(pct)}%
    </span>
  );
}

export function Skeleton({ w = "w-24" }) {
  return <div className={`h-6 ${w} animate-pulse rounded bg-slate-100`} />;
}

export function LiveStatus({ loading, error }) {
  if (error) return <span className="text-rose-500">Offline</span>;
  if (loading) return <span className="text-amber-500">Syncing...</span>;
  return <span className="text-emerald-500">Live</span>;
}

export function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Exports data to a real Excel (.xlsx) file
 * Removes internal fields like 'color'
 */
export function exportToExcel(filename, rows) {
  if (!rows || !rows.length) return;

  // 1. Define fields to exclude from the Excel file
  const excludedFields = [
    'color', 'share', 'isFamily', 'isShopify', 'index', 'memberCount', 
    'orders', 'segment', 'skuCount', 'trendPct', 'rank', 'forecastRevenue',
    'forecastUnits', 'forecastMethod', 'forecastConfidence', 'hasNewProducts',
    'hasSeasonalProducts', 'projectedAnnualUnits', 'projectedAnnualRevenue'
  ];

  // 2. Prepare clean data
  const cleanRows = rows.map(r => {
    const cleanObj = {};
    Object.keys(r).forEach(key => {
      if (!excludedFields.includes(key)) {
        // Create a friendly header (e.g., "unitsSold" -> "Units Sold")
        const friendlyHeader = key
          .replace(/([A-Z])/g, ' $1') // Add space before capitals
          .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
        
        cleanObj[friendlyHeader] = r[key];
      }
    });
    return cleanObj;
  });

  // 3. Create a Worksheet
  const worksheet = XLSX.utils.json_to_sheet(cleanRows);

  // 4. Set Column Widths (Optional: makes the file look better)
  const objectMaxLength = [];
  cleanRows.forEach(row => {
    Object.keys(row).forEach((key, i) => {
      const value = row[key] ? row[key].toString() : "";
      objectMaxLength[i] = Math.max(objectMaxLength[i] || 10, value.length + 2);
    });
  });
  worksheet["!cols"] = objectMaxLength.map(w => ({ width: w }));

  // 5. Create a Workbook and append the sheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Data");

  // 6. Generate and Download the File
  // Ensure filename ends with .xlsx
  const finalFilename = filename.toLowerCase().endsWith('.xlsx') 
    ? filename 
    : `${filename}.xlsx`;

  XLSX.writeFile(workbook, finalFilename);
}

export function exportCsv(filename, rows) {
  if (!rows || !rows.length) return;

  // 1. Get keys and filter out fields you don't want in the Excel/CSV file
  const excludedFields = ['color', 'share'];
  const keys = Object.keys(rows[0]).filter(k => !excludedFields.includes(k));

  // 2. Build the CSV content
  const csv = [
    // Header row (Capitalized for better look in Excel)
    keys.map(k => k.charAt(0).toUpperCase() + k.slice(1)).join(","),
    
    // Data rows
    ...rows.map(r => 
      keys.map(k => {
        const val = r[k] === null || r[k] === undefined ? '' : r[k];
        // Escape double quotes and wrap in quotes to handle commas within values
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(",")
    )
  ].join("\n");

  // 3. Trigger Download
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); // Required for some browsers
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ForecastCell({ value, confidence, method, kind }) {
  const methodLabels = {
    "seasonal-historical": "📅 Seasonal — avg of same month prior years",
    "seasonal-off-period": "❄️ Off-season — outside active months",
    "new-product-launch-rate": "🆕 New — daily rate since launch",
    "hybrid-historical-trend": "🔀 Hybrid — 60% historical + 40% trend",
    "trended-daily-rate": "📈 Daily rate × growth trend",
    "mixed": "🧩 Mixed methods across SKUs",
    "no-history": "❓ No historical data",
  };

  const confidenceColor =
    confidence >= 80 ? "text-emerald-600" :
      confidence >= 65 ? "text-indigo-600" :
        confidence >= 50 ? "text-amber-600" : "text-slate-500";

  return (
    <div
      className="inline-flex flex-col items-end gap-0.5 cursor-help"
      title={`${methodLabels[method] || method}\nConfidence: ${confidence}%`}
    >
      <span className={`inline-flex items-center gap-1 font-semibold ${kind === "revenue" ? "text-emerald-600" : "text-indigo-600"}`}>
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
        {kind === "revenue" ? formatCurrency(value || 0) : formatNumber(value || 0)}
      </span>
      <span className={`text-[9px] font-bold uppercase tracking-wider ${confidenceColor}`}>
        {confidence}% conf
      </span>
    </div>
  );
}

export function ForecastLegend() {
  return (
    <div className="mt-4 rounded-lg bg-slate-50 border border-slate-100 p-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 text-[10px] text-slate-600">
        <div className="flex items-start gap-1.5">
          <span>🆕</span>
          <span><strong>New:</strong> anchored to first sale date × growth trend</span>
        </div>
        <div className="flex items-start gap-1.5">
          <span>📅</span>
          <span><strong>Seasonal:</strong> uses same-month history from prior years</span>
        </div>
        <div className="flex items-start gap-1.5">
          <span>🔀</span>
          <span><strong>Hybrid:</strong> 60% historical + 40% trend blend</span>
        </div>
        <div className="flex items-start gap-1.5">
          <span>📈</span>
          <span><strong>Trended:</strong> daily rate × 90-day growth</span>
        </div>
      </div>
    </div>
  );
}

export function NewBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-blue-700 border border-blue-100">
      🆕 New
    </span>
  );
}

export function SeasonalBadge() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700 border border-amber-100">
      📅 Seasonal
    </span>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-tight text-slate-500">{label}</label>
      {children}
    </div>
  );
}